import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Ban, Undo2 } from "lucide-react";

export default function BlockedUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/users/blocked")
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const unblock = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/users/${id}/block`);
      toast.success("Unblocked");
      setItems((prev) => prev.filter((i) => i.profile.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="blocked-users-page">
      <div className="flex items-center gap-3 mb-8">
        <Ban className="w-8 h-8 text-[#6B1D2F]" />
        <div>
          <h1 className="font-serif text-3xl font-semibold text-slate-900">Blocked Members</h1>
          <p className="text-slate-600">Members you've blocked cannot contact you or view your details. Unblock any time.</p>
        </div>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-500 border-[#EAE3D2] bg-white">Loading…</Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-600 border-[#EAE3D2] bg-white" data-testid="blocked-empty">
          <Ban className="w-10 h-10 mx-auto text-[#6B1D2F]/40" />
          <p className="mt-3">You haven't blocked anyone.</p>
          <Link to="/matches" className="mt-4 inline-block"><Button variant="outline" className="rounded-full mt-3">Back to matches</Button></Link>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.profile.id} className="p-4 border-[#EAE3D2] bg-white flex items-center gap-4" data-testid={`blocked-${it.profile.id}`}>
              <div className="w-12 h-12 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center font-serif overflow-hidden">
                {it.profile.profile_photo_path
                  ? <img src={fileUrl(it.profile.profile_photo_path)} alt="" className="w-full h-full object-cover grayscale" />
                  : (it.profile.full_name?.[0] || "?")}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{it.profile.full_name || "Member"}</p>
                <p className="text-xs text-slate-500">Blocked {new Date(it.blocked_at).toLocaleDateString()}</p>
              </div>
              <Button
                data-testid={`unblock-${it.profile.id}`}
                onClick={() => unblock(it.profile.id)}
                disabled={busy === it.profile.id}
                variant="outline"
                className="rounded-full"
              >
                <Undo2 className="w-4 h-4 mr-1" />Unblock
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
